import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Table,
} from "reactstrap";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";

const FiscalYearForm = ({ isOpen, toggle, selectedItem, refetch }) => {
  const [loading, setLoading] = useState(false);

  const VOUCHER_TYPES = ["JOURNAL", "INCOME", "PAYMENT"];

  const validationSchema = Yup.object({
    code: Yup.string().required("Code is required"),
    name: Yup.string().required("Name is required"),
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date().required("End Date is required").min(
      Yup.ref("startDate"),
      "End Date must be after Start Date"
    ),
    isActive: Yup.boolean(),
    voucherTypeConfigs: Yup.array().of(
      Yup.object({
        voucherType: Yup.string().required(),
        prefix: Yup.string().required("Prefix is required"),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      code: selectedItem?.code || "",
      name: selectedItem?.name || "",
      startDate: selectedItem?.startDate ? selectedItem.startDate.substring(0, 10) : "",
      endDate: selectedItem?.endDate ? selectedItem.endDate.substring(0, 10) : "",
      isActive: selectedItem?.isActive ?? false,
      voucherTypeConfigs: selectedItem?.voucherTypeConfigs || [
        { voucherType: "JOURNAL", prefix: "" },
      ],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (selectedItem) {
          await FiscalYearService.update(selectedItem.code, values);
          toast.success("Fiscal Year updated successfully");
        } else {
          await FiscalYearService.create(values);
          toast.success("Fiscal Year created successfully");
        }
        refetch();
        toggle();
      } catch (error) {
        console.error("Submission error:", error);
        toast.error(error.response?.data?.message || "Failed to save fiscal year");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="border-bottom-0">
        {selectedItem ? "Edit Fiscal Year" : "Add Fiscal Year"}
      </ModalHeader>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <ModalBody className="py-4">
            <Row>
              <Col lg={6}>
                <FormGroup>
                  <Label for="code" className="fw-semibold small text-uppercase text-muted">
                    Code <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="code"
                    name="code"
                    type="text"
                    placeholder="e.g. 1000"
                    className="form-control-lg border-2"
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.code && !!formik.errors.code}
                    disabled={!!selectedItem}
                  />
                  <FormFeedback>{formik.errors.code}</FormFeedback>
                </FormGroup>
              </Col>
              <Col lg={6}>
                <FormGroup>
                  <Label for="name" className="fw-semibold small text-uppercase text-muted">
                    Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="e.g. 2026/2027"
                    className="form-control-lg border-2"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  <FormFeedback>{formik.errors.name}</FormFeedback>
                </FormGroup>
              </Col>
              <Col lg={6}>
                <FormGroup>
                  <Label for="startDate" className="fw-semibold small text-uppercase text-muted">
                    Start Date <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    className="form-control-lg border-2"
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.startDate && !!formik.errors.startDate}
                  />
                  <FormFeedback>{formik.errors.startDate}</FormFeedback>
                </FormGroup>
              </Col>
              <Col lg={6}>
                <FormGroup>
                  <Label for="endDate" className="fw-semibold small text-uppercase text-muted">
                    End Date <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    className="form-control-lg border-2"
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.endDate && !!formik.errors.endDate}
                  />
                  <FormFeedback>{formik.errors.endDate}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-uppercase text-primary small mb-0">Voucher Type Configurations</h6>
                <Button
                  color="soft-primary"
                  size="sm"
                  type="button"
                  onClick={() => formik.setFieldValue("voucherTypeConfigs", [
                    ...formik.values.voucherTypeConfigs,
                    { voucherType: "", prefix: "" }
                  ])}
                >
                  <i className="ri-add-line me-1"></i> Add Config
                </Button>
              </div>
              <div className="table-responsive border rounded">
                <Table className="table-nowrap mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="px-3" width="45%">Voucher Type</th>
                      <th className="px-3" width="45%">Prefix</th>
                      <th className="px-3 text-end" width="10%"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.voucherTypeConfigs.map((config, index) => (
                      <tr key={index}>
                        <td className="px-3">
                          <Input
                            name={`voucherTypeConfigs[${index}].voucherType`}
                            type="select"
                            className="form-select-sm border-2"
                            value={formik.values.voucherTypeConfigs[index].voucherType}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.voucherTypeConfigs?.[index]?.voucherType &&
                              !!formik.errors.voucherTypeConfigs?.[index]?.voucherType
                            }
                          >
                            <option value="">Select Type</option>
                            {VOUCHER_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Input>
                        </td>
                        <td className="px-3">
                          <Input
                            name={`voucherTypeConfigs[${index}].prefix`}
                            type="text"
                            placeholder="Prefix"
                            className="form-control-sm border-2"
                            value={formik.values.voucherTypeConfigs[index].prefix}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.voucherTypeConfigs?.[index]?.prefix &&
                              !!formik.errors.voucherTypeConfigs?.[index]?.prefix
                            }
                          />
                        </td>
                        <td className="px-3 text-end">
                          <Button
                            color="soft-danger"
                            size="sm"
                            type="button"
                            onClick={() => {
                              const newConfigs = [...formik.values.voucherTypeConfigs];
                              newConfigs.splice(index, 1);
                              formik.setFieldValue("voucherTypeConfigs", newConfigs);
                            }}
                            disabled={formik.values.voucherTypeConfigs.length === 1}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="border-top-0 d-flex gap-2">
            <Button color="light" size="lg" className="px-4" onClick={toggle} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" size="lg" className="px-4 shadow-sm" type="submit" disabled={loading}>
              {loading ? "Saving..." : selectedItem ? "Update Fiscal Year" : "Create Fiscal Year"}
            </Button>
          </ModalFooter>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default FiscalYearForm;
